import React, { useState } from 'react';
import { LogOut, Save, User, AlertCircle, Check } from 'lucide-react';
import { getAuth, signOut, updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const SettingsTab = ({ userData, userId }) => {
  const [name, setName] = useState(userData?.name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      // Redirect will happen automatically due to auth state change
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out. Please try again.');
    }
  };
  
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      setSuccess(false);
      
      // Update user document in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        name
      });
      
      // Update auth profile if available
      const auth = getAuth();
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: name
        });
      }
      
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div>
      <h3 style={{ 
        margin: '0 0 1.5rem 0', 
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: 'white'
      }}>
        Settings
      </h3>
      
      <form onSubmit={handleSaveProfile}>
        {/* Profile Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#999'
          }}>
            Profile Settings
          </h4>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              color: '#999'
            }}>
              Name
            </label>
            <input 
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                fontSize: '1rem'
              }}
              placeholder="Your name"
              required
            />
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              color: '#999'
            }}>
              Handle
            </label>
            <div style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#333',
              color: '#aaa',
              border: 'none',
              borderRadius: '0.25rem',
              fontSize: '1rem',
              opacity: 0.7
            }}>
              @{userData?.handle || 'username'}
            </div>
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.8rem',
              color: '#777'
            }}>
              Your handle cannot be changed
            </p>
          </div>
          
          {error && (
            <div style={{
              backgroundColor: 'rgba(255, 107, 107, 0.1)',
              border: '1px solid #ff6b6b',
              borderRadius: '0.25rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#ff6b6b'
            }}>
              <AlertCircle size={20} />
              {error}
            </div>
          )}
          
          {success && (
            <div style={{
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              border: '1px solid #4caf50',
              borderRadius: '0.25rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#4caf50'
            }}>
              <Check size={20} />
              Profile updated successfully!
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#6600CC',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <Save size={20} />
            {isLoading ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
      
      {/* Account Settings */}
      <div>
        <h4 style={{ 
          margin: '0 0 1rem 0', 
          fontSize: '1rem',
          fontWeight: 'bold',
          color: '#999'
        }}>
          Account Settings
        </h4>
        
        <button
          onClick={handleSignOut}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: 'transparent',
            color: '#ff6b6b',
            border: '1px solid #ff6b6b',
            borderRadius: '0.25rem',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <LogOut size={20} />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default SettingsTab; 